import React, {
    useContext,
    useLayoutEffect,
    useEffect,
    useRef,
    useMemo,
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { sortDishesByPriorityAndVisibility } from 'app/utils/dish';
import { isStandaloneApp } from 'app/utils/browser';

import { LocaleContext } from 'app/containers/LocaleProvider';
import Responsive from 'app/components/Responsive';
import { isDesktop } from 'app/utils/resolution';
import { ConfigContext } from '../ConfigProvider';

import Dish from '../Dish';
import { DishCategoryTextLine } from './DishCategoryTextLine';
import { NYNewbieBanner } from './NYNewbieBanner';


const sortDishes = (dishes) => {
    const orderKey = !isDesktop() && dishes.length && 'orderNumberMobile' in dishes[0]
        ? 'orderNumberMobile'
        : 'orderNumber';
    const isOrderDefine = dishes.every((dish) => dish.orderNumber);
    if (!isOrderDefine) {
        return sortDishesByPriorityAndVisibility(dishes);
    }

    return dishes.slice().sort((dish1, dish2) => dish1[orderKey] - dish2[orderKey]);
};


export default function DishCategory({
    id,
    title: titleRu = '',
    titleEn = '',
    description: descriptionRu = '',
    descriptionMobile: descriptionMobileRu = '',
    descriptionEn = '',
    descriptionMobileEn = '',
    DescriptionComponent = null,
    isFirstCategory = false,
    isLastCategory = false,
    dishes,
    basketDishes,
    buttonsDisabled = false,
    canChangePortionsCount = false,
    areDetailsEnabled = false,
    smallCategoryTitlesInMobile = false,
    colors = 'default',
    promoCheckoutDishObserver,
    isNutritionEnabled = false,
    enabledDishesByBasket = false,
}) {
    const containerRef = useRef(null);

    const categoryId = `category-${id}`;

    useLayoutEffect(() => {
        if (window.location.hash === `#${categoryId}`) {
            try {
                /**
                * Без таймаута в сафари скролл происходит раньше, чем отрендерятся все категории,
                * из-за чего высота контента меняется уже после скролла
                */
                setTimeout(() => {
                    containerRef.current.scrollIntoView(true);
                });
            } catch (e) {
                console.log(e);
            }
        }
    }, [categoryId]);

    const { locale } = useContext(LocaleContext);
    const {
        v2Layout,
        pageId,
    } = useContext(ConfigContext);

    const titles = {
        ru: titleRu,
        en: titleEn,
    };
    const title = titles[locale];

    const descriptions = {
        ru: descriptionRu,
        en: descriptionEn,
    };
    const description = descriptions[locale];

    const descriptionsMobile = {
        ru: descriptionMobileRu || descriptionRu,
        en: descriptionMobileEn || descriptionEn,
    };
    const descriptionMobile = descriptionsMobile[locale];

    const sortedDishes = (pageId === 'christmas' || pageId === 'old_ny')
        ? dishes
        : sortDishes(dishes);

    const dishesById = useMemo(() => sortedDishes.reduce((acc, dish) => {
        acc[dish.id] = dish;
        return acc;
    }, {}), [sortedDishes]);

    const categoryDishesIds = useMemo(
        () => dishes.map((d) => d.id),
        [dishes],
    );

    const basketDishesById = useMemo(() => basketDishes.reduce((acc, basketDish) => {
        acc[basketDish.id] = basketDish;
        return acc;
    }, {}), [basketDishes]);

    const basketDishesIds = useMemo(
        () => basketDishes.map((d) => d.id),
        [basketDishes],
    );

    const basketDishesIdsFromCategory = useMemo(
        () => basketDishesIds.filter((id) => categoryDishesIds.includes(id)),
        [basketDishesIds, categoryDishesIds],
    );

    const isDishRemovingDisabled = (pageId === 'romantic' && id === 'dessert')
        || (pageId === 'romantic' && basketDishesIdsFromCategory?.length === 1);

    const getBasketDish = (dish) => {
        if (!dish) return undefined;

        if (dish.isSet) {
            return dish.dishes
                ?.map((dishId) => basketDishesById[dishId])
                .find(Boolean);
        }

        return basketDishesById[dish.id];
    };

    useEffect(() => {
        if (isFirstCategory) {
            const staticStub = document.querySelector('.first-category-static-stub');
            if (staticStub) {
                staticStub.style.display = 'none';
            }
        }
        // if (id === 'dessert') {
        //     const staticStub = document.querySelector('.rest-category-static-stub');
        //     if (staticStub) {
        //         staticStub.style.display = 'none';
        //     }
        // }
    }, [isFirstCategory, id]);

    const wrapperClasses = classNames({
        'ny__set__dishes-container': true,
        noBorder: pageId === 'romantic',
    });

    if (isFirstCategory) {
        return (
            <div className={wrapperClasses}>
                <div className={`ny__set__dishes-list ${v2Layout && 'ny__set__dishes-list__v2'}`} data-toggle-details-parent>
                    {sortedDishes.map((dish, index) => {
                        const basketDish = getBasketDish(dish) || { portion: 0 };

                        const isOddDishNumber = (sortedDishes.length % 2) === 1;
                        const isLastDish = index === (sortedDishes.length - 1);
                        const isCentered = isOddDishNumber && isLastDish;

                        if (enabledDishesByBasket && basketDish.portion === 0) {
                            return null;
                        }

                        if (pageId === 'squid-game' && dish.id === '4978') {
                            return null;
                        }

                        return (
                            <Dish
                                layout="default"
                                index={index}
                                isDishRemovingDisabled={isDishRemovingDisabled}
                                {...dish}
                                dishesById={dishesById}
                                basketDishesById={basketDishesById}
                                portion={basketDish.portion}
                                buttonsDisabled={buttonsDisabled}
                                isFirstCategory={isFirstCategory}
                                isCentered={isCentered}
                                key={dish.id}
                                canChangePortionsCount={canChangePortionsCount}
                                areDetailsEnabled={areDetailsEnabled}
                                colors={colors}
                                promoCheckoutDishObserver={promoCheckoutDishObserver}
                                isNutritionEnabled={isNutritionEnabled}
                                isLastDish={isLastDish}
                                categoryId={id}
                            />
                        );
                    })}
                </div>
            </div>
        );
    }

    const rootClasses = classNames({
        ny__set: true,
        thin: (id === 'niceStuff' || id === 'sauсes') && 'thin',
        'first-set': isFirstCategory,
        'last-set': !isFirstCategory && isLastCategory,
        [pageId]: true,
    });
    const titleClasses = classNames({
        ny__set__title: true,
        'big-desktop': isFirstCategory,
        'big-mobile': isFirstCategory && !smallCategoryTitlesInMobile,
        'romantic-set-list': pageId === 'romantic',
        'main-card-title': pageId === 'romantic',
    });
    const descriptionClasses = classNames({
        ny__set__description: true,
        big: isFirstCategory,
    });

    if (!sortedDishes.length) return null;

    return (
        <section className={rootClasses}>
            {pageId !== 'hp' && (
                <div className={pageId !== 'romantic' && 'ny__set__desc-container content-container'}>
                    <h2
                        className={titleClasses}
                        id={categoryId}
                        data-category-title-id={id}
                        ref={containerRef}
                    >
                        <DishCategoryTextLine>
                            {title}
                        </DishCategoryTextLine>
                    </h2>
                    {pageId !== 'romantic' && (
                        <div className={descriptionClasses}>
                            {DescriptionComponent && <DescriptionComponent />}
                            {!DescriptionComponent && (descriptionMobile || description) && (
                                <Responsive
                                    mobile={<DishCategoryTextLine>{descriptionMobile}</DishCategoryTextLine>}
                                    desktop={<DishCategoryTextLine>{description}</DishCategoryTextLine>}
                                />
                            )}
                        </div>
                    )}
                    {id === 'salads' && <NYNewbieBanner />}
                </div>
            )}

            <div className={`ny__set__dishes-container ${(id === 'niceStuff' || id === 'sauсes') && 'thin'}`}>
                <ul
                    className={`ny__set__dishes-list ${v2Layout && 'ny__set__dishes-list__v2'}
                     ${(id === 'niceStuff' || id === 'sauсes') && 'ny__set__dishes-list__thin'} ${(pageId === 'romantic') && 'romantic'}`}
                    data-toggle-details-parent
                >
                    {sortedDishes.map((dish, index) => {
                        const basketDish = getBasketDish(dish) || { portion: 0 };

                        const isOddDishNumber = (sortedDishes.length % 2) === 1;
                        const isLastDish = index === (sortedDishes.length - 1);
                        const isCentered = isOddDishNumber && isLastDish;

                        if (enabledDishesByBasket && basketDish.portion === 0) {
                            return null;
                        }

                        if (isStandaloneApp() && dish.id === 'ym') {
                            return null;
                        }

                        return (
                            <Dish
                                index={index}
                                {...dish}
                                isDishRemovingDisabled={isDishRemovingDisabled}
                                portion={basketDish.portion}
                                buttonsDisabled={buttonsDisabled}
                                isFirstCategory={isFirstCategory}
                                isCentered={isCentered}
                                key={dish.id}
                                canChangePortionsCount={canChangePortionsCount}
                                areDetailsEnabled={areDetailsEnabled}
                                promoCheckoutDishObserver={promoCheckoutDishObserver}
                                dishesById={dishesById}
                                basketDishesById={basketDishesById}
                                isLastDish={isLastDish}
                                categoryId={id}
                                colors={colors}
                            />
                        );
                    })}
                </ul>
            </div>
        </section>
    );
}

DishCategory.propTypes = {
    id: PropTypes.string.isRequired,
    colors: PropTypes.string,
    title: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
    ]),
    titleEn: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
    ]),
    description: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    descriptionMobile: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    descriptionEn: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    descriptionMobileEn: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    DescriptionComponent: PropTypes.func,
    dishes: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    basketDishes: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    isFirstCategory: PropTypes.bool,
    isLastCategory: PropTypes.bool,
    buttonsDisabled: PropTypes.bool,
    canChangePortionsCount: PropTypes.bool,
    areDetailsEnabled: PropTypes.bool,
    smallCategoryTitlesInMobile: PropTypes.bool,
    isNutritionEnabled: PropTypes.bool,
    promoCheckoutDishObserver: PropTypes.shape({ observe: PropTypes.func }).isRequired,
    enabledDishesByBasket: PropTypes.bool,
};
